import { Capacitor } from '@capacitor/core'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Filesystem } from '@capacitor/filesystem'
import { TextRecognition } from '@capacitor-mlkit/text-recognition'
import { leseMrz, uebernehmbar } from './mrz'
import { findeMrz } from './mrzZeilen'

/**
 * Einen Ausweis scannen und die geprüften Felder zurückgeben.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  DAS BILD VERLÄSST DAS GERÄT NICHT — UND BLEIBT AUCH NICHT LIEGEN
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Die Erkennung läuft über ML Kit auf der Hardware des Geräts. Es wird nichts
 * hochgeladen, nichts an einen Dienst geschickt, und es gibt keinen Schlüssel
 * für irgendetwas.
 *
 * Bleibt die Aufnahme selbst: die Kamera legt sie als Datei im privaten
 * Zwischenspeicher der App ab, sonst könnte die Erkennung sie nicht lesen.
 * Diese Datei wird nach der Erkennung GELÖSCHT, im `finally` — auch dann, wenn
 * die Erkennung scheitert. Ein Ausweisfoto, das im Zwischenspeicher liegen
 * bleibt, ist genau die Haftungsfrage, die dieser Vorgang vermeiden will.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  NUR IN DER APP
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ML Kit gibt es nur nativ. Im Browser ist `verfuegbar()` falsch, und der Knopf
 * erscheint gar nicht erst — die Gastseite bleibt unverändert. Warum das die
 * bessere Aufteilung ist und nicht bloß eine Einschränkung, steht in
 * `docs/adr/004`: der Gast, der vorab ausfüllt, hat seinen Ausweis meist nicht
 * zur Hand; der Vermieter bei der Anreise hat ihn in der Hand.
 */

export type ScanFelder = ReturnType<typeof uebernehmbar>

export interface ScanErgebnis {
  /** Was übernommen werden darf — nur Felder mit stimmiger Prüfziffer. */
  felder: ScanFelder
  /** Wurde überhaupt eine MRZ gefunden? */
  erkannt: boolean
  /** Stimmten alle Prüfziffern zusammen? */
  vollstaendig: boolean
}

/** Gibt es den Scanner hier? Im Browser nein. */
export function verfuegbar(): boolean {
  return Capacitor.isNativePlatform()
}

/**
 * Kamera öffnen, erkennen, Datei löschen, Felder zurückgeben.
 *
 * Wirft nur, wenn der Benutzer abbricht oder die Berechtigung fehlt — das
 * behandelt der Aufrufer als „nichts passiert", nicht als Fehler. Ein
 * unlesbarer Ausweis dagegen ist kein Wurf, sondern ein Ergebnis mit
 * `erkannt: false`: der Vermieter soll „noch einmal" lesen und nicht
 * „Fehler 500".
 */
export async function scanneAusweis(): Promise<ScanErgebnis> {
  const leer: ScanErgebnis = { felder: {}, erkannt: false, vollstaendig: false }
  if (!verfuegbar()) return leer

  const foto = await Camera.getPhoto({
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
    // NICHT in die Fotomediathek. Ein Ausweisfoto hat in der Galerie des
    // Vermieters nichts verloren — und dort käme es auch in jede Sicherung.
    saveToGallery: false,
    // Genug für die MRZ, wenig genug, dass die Erkennung schnell bleibt.
    quality: 85,
    allowEditing: false,
  })

  const pfad = foto.path ?? foto.webPath
  if (!pfad) return leer

  try {
    const ergebnis = await TextRecognition.processImage({ path: pfad })

    // Sowohl den Gesamttext als auch die einzelnen Zeilen anbieten: manche
    // Aufnahmen liefern die MRZ sauber getrennt, andere als einen Block.
    const bloecke = [
      ergebnis.text,
      ...ergebnis.blocks.flatMap((b) => b.lines.map((l) => l.text)),
    ]

    const zeilen = findeMrz(bloecke)
    if (zeilen.length === 0) return leer

    const m = leseMrz(zeilen)
    if (!m) return leer

    return {
      felder: uebernehmbar(m),
      erkannt: true,
      vollstaendig: m.gesamtpruefung && m.ausweisnummer.geprueft === true
        && m.geburtsdatum.geprueft === true,
    }
  } finally {
    // Immer — auch wenn die Erkennung geworfen hat.
    if (foto.path) {
      await Filesystem.deleteFile({ path: foto.path }).catch(() => {
        // Schlägt das Löschen fehl, ist das kein Grund, den Scan scheitern zu
        // lassen. Es gehört aber ins Protokoll: eine Datei, die liegen bleibt,
        // ist ein Ausweisfoto, das liegen bleibt.
        console.error('[StaySuite] Aufnahme konnte nicht gelöscht werden')
      })
    }
  }
}
